/**
 * Clients with a website on their card that nobody watches yet, one click each
 * to start watching.
 *
 * This is how the desk fills on day one: the websites are already typed into
 * the client register, and retyping them into a dialog is where typos come from.
 * The address shown is the one the server would save (`suggestion.url`), not
 * the raw field, so the operator sees exactly what will be checked. A website
 * that does not parse as an address is listed with no button - it needs fixing
 * on the client card first, and the row says so.
 *
 * Only drawn for somebody who may add sites. The first check runs as part of
 * the add, and its answer is said straight away: a site added while it refuses
 * https should not look like a success.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { createMonitor, monitorErrorKey, type MonitorSuggestion } from "../../../lib/monitoring";
import { Btn, CardHead, useToast } from "../../ui";
import { codeText, type T } from "./words";

/** More than this and the card becomes the page; the rest are behind "show all". */
const SHOWN = 6;

export function SuggestionsCard({
  suggestions,
  t,
  onAdded,
}: {
  suggestions: MonitorSuggestion[];
  t: T;
  onAdded: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [all, setAll] = useState(false);

  if (suggestions.length === 0) return null;
  const shown = all ? suggestions : suggestions.slice(0, SHOWN);

  async function add(s: MonitorSuggestion) {
    if (!s.url) return;
    setBusy(s.clientId);
    try {
      const res = await createMonitor({ clientId: s.clientId, url: s.url });
      const said = codeText(t, res.site.lastCode);
      toast(
        res.site.state === "up"
          ? t("monitoring.toast.addedUp", { host: res.site.host })
          : res.site.lastCheckAt === null
            ? t("monitoring.toast.addedNotRun", { host: res.site.host })
            : t("monitoring.toast.addedOther", { host: res.site.host, what: said ?? t("monitoring.state.unknown") }),
      );
      onAdded();
    } catch (err) {
      toast(t(`monitoring.error.${monitorErrorKey(err)}`));
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="crm-card mt-4">
      <CardHead
        title={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="size-4 text-[var(--color-primary)]" strokeWidth={1.8} />
            {t("monitoring.suggest.title")}
          </span>
        }
        sub={t("monitoring.suggest.sub", { n: suggestions.length })}
      />
      <ul className="flex flex-col px-[18px] pb-4">
        {shown.map((s) => (
          <li
            key={s.clientId}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-2xl px-2 py-2.5 hover:bg-[var(--crm-sunk)]"
          >
            <span className="min-w-0 flex-1">
              <Link to={`/clients/${s.clientId}`} className="crm-prow-n crm-trunc block hover:underline">
                {s.clientName}
              </Link>
              <span className="crm-prow-s crm-trunc block">
                {s.url ?? t("monitoring.suggest.unparsable", { website: s.website })}
              </span>
            </span>
            {s.url ? (
              <Btn small disabled={busy !== null} onClick={() => void add(s)}>
                {busy === s.clientId ? (
                  <Loader2 className="size-[15px] animate-spin" />
                ) : (
                  <Plus className="size-[15px]" strokeWidth={1.8} />
                )}
                {t("monitoring.suggest.add")}
              </Btn>
            ) : null}
          </li>
        ))}
      </ul>
      {suggestions.length > SHOWN ? (
        <div className="flex justify-center px-[26px] pb-5">
          <Btn tone="plain" small onClick={() => setAll((v) => !v)}>
            {all ? t("monitoring.suggest.fewer") : t("monitoring.suggest.all", { n: suggestions.length })}
          </Btn>
        </div>
      ) : null}
    </section>
  );
}
